/**
 * The path switch.
 *
 * STILL NOT A ROUTER LIBRARY, and Story 3.8 is the story that was told to
 * decide. It has now seen the whole page inventory: twelve static paths, no
 * nested routes until Epic 6's editor tabs, no route params until the leads
 * drawer is deep-linked (PRD §7.4). A library buys route params, nesting and a
 * history API — three things nothing here uses yet, in exchange for a
 * dependency the funnel editor then has to live with. Epic 6 is the story that
 * will have a real reason; this one does not, and adopting a router because the
 * page count grew would be a decision made on the wrong evidence.
 *
 * The four auth screens sit OUTSIDE the shell by nature — a signed-out person
 * never sees a sidebar — which is what `shell: false` marks.
 *
 * `wrangler.jsonc` sets `not_found_handling: "single-page-application"`, so
 * every path below serves `index.html` and is resolved here rather than 404ing
 * on refresh.
 *
 * Stories 3.3, 3.8.
 */
import { AcceptInvitation } from './auth/AcceptInvitation'
import { LogIn } from './auth/LogIn'
import { ResetPassword } from './auth/ResetPassword'
import { SignUp } from './auth/SignUp'
import { VerifyEmail } from './auth/VerifyEmail'
import { Dashboard } from './pages/Dashboard'
import { Deliveries } from './pages/Deliveries'
import { Editor, editorPath } from './pages/Editor'
import { Funnels } from './pages/Funnels'
import { Leads } from './pages/Leads'
import { LeadDetailRoute } from './pages/LeadDrawer'
import { Library } from './pages/Library'
import { ReviewQueue } from './pages/ReviewQueue'
import { UsageCost } from './pages/UsageCost'
import { AuditLog } from './pages/settings/AuditLog'
import { Billing } from './pages/settings/Billing'
import { Compliance } from './pages/settings/Compliance'
import { Credentials } from './pages/settings/Credentials'
import { Integrations } from './pages/Integrations'
import { Members } from './pages/settings/Members'
import { Profile } from './pages/settings/Profile'
import { Workspaces } from './pages/settings/Workspaces'
import { HOME_PATH } from './shell/nav'

type Route = {
  path: string
  element: () => JSX.Element
  title: string
  /** False for the signed-out screens, which have no chrome around them. */
  shell: boolean
  /**
   * Epic 4's screens swap the main sidebar for the settings one — Story 4.1,
   * `settings-members.html`. Absent means the ordinary shell.
   */
  settings?: boolean
  /**
   * Story 6.2's editor: inside the shell for `/v1/me` and the workspace, and
   * without its top bar or sidebar, because the editor draws its own.
   */
  bare?: boolean
  /**
   * A path this route claims that `path` does not spell literally.
   *
   * THE ONE DYNAMIC ROUTE, and the header above said Epic 6 would be the story
   * with a real reason. It is — `/funnels/{fid}` and `/funnels/{fid}/{tab}` —
   * and a router library is still not that reason. A library buys route params,
   * nesting and a history API; this needs the first, has no use for the second,
   * and deliberately does not want the third, because the editor's tab is not a
   * navigation and rewrites the URL with `replaceState`. One predicate is the
   * whole of what was missing.
   */
  match?: (pathname: string) => boolean
}

export const ROUTES: Route[] = [
  { path: '/signup', element: SignUp, title: 'Create your Leadgible account', shell: false },
  { path: '/login', element: LogIn, title: 'Log in', shell: false },
  { path: '/reset', element: ResetPassword, title: 'Reset your password', shell: false },
  { path: '/verify', element: VerifyEmail, title: 'Confirm your email address', shell: false },
  { path: '/invite', element: AcceptInvitation, title: 'Accept your invitation', shell: false },

  // The TEN nav destinations, in DECISION-018's order as DECISION-087 amended
  // it and Story 22.3 extended it at M2. Every one is a real page — Story
  // 12.1's dashboard was the last placeholder to fall.
  // Story 12.1. The needs-attention feed lands on the same screen in 12.2 and
  // the Verdict headline in 12.4; all three read one `GET …/dashboard`.
  { path: '/dashboard', title: 'Dashboard', shell: true, element: Dashboard },
  // Story 8.2.
  { path: '/leads', title: 'Leads', shell: true, element: Leads },
  /**
   * Story 8.4. Declared AFTER `/leads` so the exact path still wins, the same
   * way `/funnels/:fid` is declared after `/funnels`.
   *
   * A ROUTE and not list state, because PRD §7.4 requires the drawer be
   * deep-linkable: §3.10's signed one-click override links from notifications
   * target it directly, and a link in an email cannot go through the list.
   */
  {
    path: '/leads/:lid',
    title: 'Lead',
    shell: true,
    element: LeadDetailRoute,
    match: (pathname) => /^\/leads\/[\w-]+\/?$/.test(pathname),
  },
  // Story 22.3 — PRD §7.4's M2 page, reached from Work. Each row opens the
  // drawer above, at `/leads/:lid`.
  { path: '/review-queue', title: 'Review queue', shell: true, element: ReviewQueue },
  // Story 6.1.
  { path: '/funnels', title: 'Funnels', shell: true, element: Funnels },
  // Story 6.2. Declared AFTER `/funnels` so the exact path still wins.
  {
    path: '/funnels/:fid',
    title: 'Funnel editor',
    shell: true,
    bare: true,
    element: Editor,
    match: (pathname) => editorPath(pathname) !== null,
  },
  {
    path: '/library',
    title: 'Library',
    shell: true,
    element: Library,
  },
  // Story 4.1. Its path stays `/team` because DECISION-018 fixed the nav item
  // there; the SETTINGS nav points at the same path from the other side.
  { path: '/team', title: 'Members and roles', shell: true, settings: true, element: Members },
  // Story 11.3.
  { path: '/integrations', title: 'Integrations', shell: true, element: Integrations },
  // DECISION-087, built inside Story 12.2 — the ninth destination, and where
  // the feed's failed-delivery items resolve.
  { path: '/deliveries', title: 'Deliveries', shell: true, element: Deliveries },
  // Story 4.3.
  { path: '/settings', title: 'Profile', shell: true, settings: true, element: Profile },
  {
    path: '/billing',
    title: 'Billing and plan',
    shell: true,
    settings: true,
    element: Billing,
  },
  // Story 23.3 — Owner and Admin, refused at the API for everyone else (PRD §3.13).
  { path: '/usage', title: 'Usage & cost', shell: true, element: UsageCost },

  /**
   * The three settings destinations with no main-nav entry.
   *
   * `SETTINGS_NAV` lists six sections and Story 4.1 builds one of them, so the
   * other five need somewhere to land — a nav item that 404s on refresh is
   * worse than one that says which story brings it. Profile and Billing already
   * had routes above; these three did not.
   */
  // Story 4.2.
  { path: '/settings/workspaces', title: 'Workspaces', shell: true, settings: true, element: Workspaces },
  // Story 11.2.
  {
    path: '/settings/credentials',
    title: 'Providers and credentials',
    shell: true,
    settings: true,
    element: Credentials,
  },
  // Story 26.6 — PRD §7.6's Compliance row.
  { path: '/settings/compliance', title: 'Compliance', shell: true, settings: true, element: Compliance },
  // Story 4.5.
  { path: '/settings/audit', title: 'Audit log', shell: true, settings: true, element: AuditLog },
]

/** The signed-out landing path. */
export const DEFAULT_PATH = '/signup'

export function resolve(pathname: string): Route {
  const clean = pathname.replace(/\/+$/, '') || '/'
  // `/` means the dashboard for somebody signed in; the shell sends anybody
  // else to `/login` as soon as `/v1/me` answers 401.
  if (clean === '/') return ROUTES.find((r) => r.path === HOME_PATH)!
  // Literal first, always. `/funnels` is a page AND the prefix of the editor's
  // pattern, and the list must not lose to the pattern that contains it.
  return (
    ROUTES.find((r) => r.path === clean) ??
    ROUTES.find((r) => r.match?.(clean)) ??
    ROUTES.find((r) => r.path === DEFAULT_PATH)!
  )
}
